import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrdersFacade } from '../../../+store/order/order.facade';
import {
  IOrder,
  ICustomerUser,
  OrderStatusKey,
  resolveOrderChargeAmount
} from '@care-portals/api-interfaces';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { APIService } from '../../../services/api/api.service';
import { ORDER_STATUSES, PAYMENT_STATUSES } from '@care-portals/data';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { OrderProductsPopoverComponent } from '../order-products-popover/order-products-popover.component';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzModalService } from 'ng-zorro-antd/modal';
import { OrderedShippedDialogComponent } from '../ordered-shipped-dialog/ordered-shipped-dialog.component';
import { getDateFormat } from '@care-portals/shared';
import { format } from 'date-fns';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { OrderedRejectedDialogComponent } from '../ordered-rejected-dailog/ordered-rejected-dialog.component';
import { OrderedCancelledDialogComponent } from '../ordered-cancelled-dailog/ordered-cancelled-dialog.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: '[portals-emr-order-row]',
  templateUrl: './order-row.component.html',
  styleUrls: ['./order-row.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzSpaceModule,
    NzButtonModule,
    NzDividerModule,
    NzPopoverModule,
    NzTagModule,
    OrderProductsPopoverComponent,
    NzToolTipModule
  ]
})
export class OrderRowComponent implements OnInit, OnDestroy {
  @Input() order: IOrder;
  @Input() hiddenColumns: string[] = [];
  @Input() showDetails: boolean = true;

  isLoading$: Observable<boolean>;
  isSaving$: Observable<boolean>;
  orders: IOrder[];
  statuses = Object.entries(ORDER_STATUSES)
    .filter(s => s[1].show)
    .sort((a, b) => (a[1].sort < b[1].sort ? -1 : 1));
  ordersProducts = {};
  _getDateFormat = getDateFormat;
  isLoading = false;
  constructor(
    public api: APIService,
    private orderFacade: OrdersFacade,
    private customerFacade: CustomerFacade,
    private modalService: NzModalService
  ) {
    this.isLoading$ = this.orderFacade.loadingOrders$;
    this.isSaving$ = this.orderFacade.savingOrder$;
  }

  get customer() {
    return this.order.customer as ICustomerUser;
  }

  get paymentStatus() {
    if (!this.order) return null;

    // order.payment object is new and did not apply to all successful payments
    // so rely on order.meta.paymentStatus for this instance
    // Can be removed later on
    if (this.order?.payment?.status) {
      return this.order.payment.status;
    } else if (this.order.meta?.paymentStatus === 'succeeded') {
      return 'paid';
    } else {
      return null;
    }
  }

  get orderSource() {
    const orderSource = this.order.source;
    if (!orderSource) return '';
    return orderSource.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  get paymentStatusInfo() {
    const status = this.paymentStatus;
    return PAYMENT_STATUSES[status];
  }

  get assignedToLabel(): string {
    const assignedTo = this.order?.assignedTo as any;
    if (!assignedTo || typeof assignedTo !== 'object') {
      return '';
    }

    const firstName = assignedTo.firstName || '';
    const lastName = assignedTo.lastName || '';
    return `${firstName}${lastName ? ` ${lastName}` : ''}`.trim();
  }

  get assignedPharmacyLabel(): string {
    const assignedPharmacy = this.order?.assignedPharmacy as any;
    if (!assignedPharmacy || typeof assignedPharmacy !== 'object') {
      return '';
    }
    return assignedPharmacy.name || '';
  }

  get paymentStatusColor(): string {
    const status = this.paymentStatus;
    return PAYMENT_STATUSES[status]?.color;
  }

  ngOnInit() { }

  ngOnDestroy(): void { }

  changeStatus(status: OrderStatusKey, data: IOrder) {
    if (status === 'rejected') {
      this.modalService.create<OrderedRejectedDialogComponent>({
        nzTitle: 'Order Rejected',
        nzContent: OrderedRejectedDialogComponent,
        nzData: { order: data },
        nzFooter: null
      });
    } else if (status === 'cancelled') {
      this.modalService.create<OrderedCancelledDialogComponent>({
        nzTitle: 'Order Cancelled',
        nzContent: OrderedCancelledDialogComponent,
        nzData: { order: data },
        nzFooter: null
      });
    } else if (status === 'shipped') {
      this.modalService.create<OrderedShippedDialogComponent>({
        nzTitle: 'Ordered Shipped',
        nzContent: OrderedShippedDialogComponent,
        nzData: {
          order: data,
          postalCode: data.shippingAddress.postalCode
        },
        nzFooter: null
      });
    } else {
      this.orderFacade.updateOrder(data.id, { status });
    }
  }

  getCustomerUrl(customerId) {
    return this.customerFacade.getCustomerUrl(customerId);
  }

  // legacy support
  openCustomer(bigcommerceId: string) {
    this.customerFacade.openCustomerByBigcommerceId(bigcommerceId);
  }

  isSubscriptionCycleOrder(order: IOrder) {
    return order.source === 'subscription_cycle';
  }

  chargeOrder(order: IOrder) {
    if (!order || !this.customer?._id) {
      return;
    }

    const orderAmount = resolveOrderChargeAmount(order);
    const formattedAmount = this.toDollars(orderAmount);
    const customerName =
      (this.customer.firstName || '') + ' ' + (this.customer.lastName || '');

    this.modalService.confirm({
      nzTitle: 'Confirm Charge Order',
      nzContent: `
        <div>
          <p>Are you sure you want to charge this order?</p>
          <p class="mt-2"><strong>Order #${order.id}</strong></p>
          <p><strong>Customer:</strong> ${customerName.trim() || 'N/A'}</p>
          <p><strong>Amount:</strong> ${formattedAmount}</p>
        </div>
      `,
      nzOkText: 'Charge Order',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzCancelText: 'Cancel',
      nzOnOk: async () => {
        this.isLoading = true;
        try {
          await this.orderFacade.chargeOrder(
            String(order.id),
            this.customer._id
          );
        } finally {
          this.isLoading = false;
        }
      }
    });
  }

  openDetails(order: IOrder) {
    if (!this.showDetails) return;
    this.orderFacade.openOrderDetails(String(order.id));
  }

  toDollars(val: string | number) {
    return '$' + parseFloat('' + val).toFixed(2);
  }

  get lastUpdateDate() {
    const date = this.order?.updatedAt || this.order?.statusHistory?.slice(-1)?.[0]?.updatedAt || '';
    return date ? format(new Date(date), getDateFormat(date)) : '';
  }

  isColumnVisible(columnKey: string): boolean {
    return !this.hiddenColumns.includes(columnKey);
  }
}
